import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { 
  Plus, Edit2, Trash2, StickyNote, Search, X, Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NOTE_COLORS = [
  { id: 'default', bg: 'bg-white', border: 'border-zinc-200', text: 'text-zinc-900' },
  { id: 'yellow', bg: 'bg-[#FEF3C7]', border: 'border-[#F59E0B]', text: 'text-[#92400E]' },
  { id: 'green', bg: 'bg-[#D1FAE5]', border: 'border-[#10B981]', text: 'text-[#065F46]' },
  { id: 'blue', bg: 'bg-[#DBEAFE]', border: 'border-[#3B82F6]', text: 'text-[#1E40AF]' },
  { id: 'pink', bg: 'bg-[#FCE7F3]', border: 'border-[#EC4899]', text: 'text-[#9D174D]' },
  { id: 'purple', bg: 'bg-[#EDE9FE]', border: 'border-[#8B5CF6]', text: 'text-[#5B21B6]' }
];

const NotesPage = () => {
  const { getAuthHeader } = useAuth();
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    color: 'default'
  });

  const fetchNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/notes`, { headers: getAuthHeader() });
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const openCreateDialog = () => {
    setEditingNote(null);
    setFormData({ title: '', content: '', color: 'default' });
    setDialogOpen(true);
  };

  const openEditDialog = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      color: note.color || 'default'
    });
    setDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!formData.title.trim()) {
      toast.error(t('notes.titleRequired'));
      return;
    }

    try {
      if (editingNote) {
        await axios.put(`${API_URL}/notes/${editingNote.id}`, formData, { headers: getAuthHeader() });
        toast.success(t('notes.updated'));
      } else {
        await axios.post(`${API_URL}/notes`, formData, { headers: getAuthHeader() });
        toast.success(t('notes.created'));
      }
      setDialogOpen(false);
      fetchNotes();
    } catch (error) {
      toast.error(t('notes.error'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/notes/${noteId}`, { headers: getAuthHeader() });
      toast.success(t('notes.deleted'));
      fetchNotes();
    } catch (error) {
      toast.error(t('notes.error'));
    }
  };

  const getColorInfo = (colorId) => NOTE_COLORS.find(c => c.id === colorId) || NOTE_COLORS[0];

  const filteredNotes = notes.filter(note => 
    note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div data-testid="notes-page" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('notes.title')}
            </h1>
            <p className="text-zinc-600 mt-1">{t('notes.subtitle')}</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('notes.newNote')}
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('notes.search')}
            className="pl-12 h-12 rounded-2xl border-zinc-200"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              <X className="w-5 h-5 text-zinc-400 hover:text-zinc-600" />
            </button>
          )}
        </div>

        {/* Notes Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">{t('common.loading')}</div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-zinc-100 rounded-2xl flex items-center justify-center">
              <StickyNote className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="font-semibold text-zinc-900 mb-1">{t('notes.noNotes')}</h3>
            <p className="text-zinc-500 text-sm">{t('notes.createFirst')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredNotes.map((note, index) => {
                const colorInfo = getColorInfo(note.color);
                return (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    className={`${colorInfo.bg} ${colorInfo.border} border-2 rounded-2xl p-5 group cursor-pointer hover:shadow-lg transition-shadow`}
                    onClick={() => openEditDialog(note)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className={`font-bold ${colorInfo.text} line-clamp-1`}>{note.title}</h3>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(note);
                          }}
                          className="p-1.5 hover:bg-white/50 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4 text-zinc-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note.id);
                          }}
                          className="p-1.5 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    <p className={`${colorInfo.text} opacity-80 text-sm line-clamp-4 whitespace-pre-wrap`}>
                      {note.content}
                    </p>
                    <div className="mt-4 pt-3 border-t border-black/10">
                      <span className="text-xs text-zinc-500">
                        {new Date(note.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Note Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" aria-describedby="note-dialog-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
                  <StickyNote className="w-5 h-5" />
                </div>
                {editingNote ? t('notes.editNote') : t('notes.newNote')}
              </DialogTitle>
              <p id="note-dialog-description" className="sr-only">
                {editingNote ? t('notes.editNote') : t('notes.newNote')}
              </p>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Title */}
              <div>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('notes.titlePlaceholder')}
                  className="h-12 text-lg font-semibold border-0 border-b-2 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D93025]"
                  data-testid="note-title-input"
                />
              </div>
              
              {/* Content */}
              <div>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={t('notes.contentPlaceholder')}
                  className="min-h-[200px] resize-none border-zinc-200"
                  data-testid="note-content-input"
                />
              </div>
              
              {/* Color Selection */}
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-2 block">{t('notes.color')}</label>
                <div className="flex gap-2">
                  {NOTE_COLORS.map(color => (
                    <button
                      key={color.id}
                      onClick={() => setFormData({ ...formData, color: color.id })}
                      className={`w-8 h-8 rounded-full ${color.bg} border-2 ${color.border} ${
                        formData.color === color.id ? 'ring-2 ring-offset-2 ring-zinc-400' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2 pt-2">
              {editingNote && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    handleDeleteNote(editingNote.id);
                    setDialogOpen(false);
                  }}
                  className="rounded-full px-4 text-red-600 hover:text-red-700 hover:bg-red-50 mr-auto"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('common.delete')}
                </Button>
              )}
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full px-6">
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleSaveNote}
                className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
              >
                <Save className="w-4 h-4 mr-2" />
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NotesPage;
